import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  Loader2,
  Phone,
  PhoneCall,
  Plus,
  Search,
  Trash2,
  Unlink,
  UserPlus,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useToast } from '../../components/ui/toast';
import {
  useAssignPoolNumber,
  useBuyPoolNumber,
  useNumberPool,
  useReleasePoolNumber,
  useSearchPlatformNumbers,
  useUnassignPoolNumber,
  type NumberPoolRow,
  type AvailableNumberRow,
} from '../../hooks/useBilling';
import { useAdminUsers } from '../../hooks/useAdminUsers';

export function AdminNumberPoolPage() {
  const pool = useNumberPool();
  const [buyDialogOpen, setBuyDialogOpen] = React.useState(false);
  const [assignTarget, setAssignTarget] = React.useState<NumberPoolRow | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Telefonnummern-Pool</h2>
          <p className="text-sm text-muted-foreground">
            Plattform-eigene Nummern auf Twilio + Telnyx. Werden bei Anfänger-Modus-Aktivierung
            automatisch zugewiesen.
          </p>
        </div>
        <Button onClick={() => setBuyDialogOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> Nummer kaufen
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <PoolStat label="Gesamt" value={pool.data?.summary.total ?? 0} />
        <PoolStat
          label="Verfügbar"
          value={pool.data?.summary.available ?? 0}
          tone="success"
        />
        <PoolStat
          label="Zugewiesen"
          value={pool.data?.summary.assigned ?? 0}
          tone="primary"
        />
        <PoolStat
          label="Released"
          value={pool.data?.summary.released ?? 0}
          tone="muted"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pool-Inhalt</CardTitle>
          <CardDescription>
            {pool.isLoading ? 'Lade …' : `${pool.data?.items.length ?? 0} Nummern`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pool.isLoading ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade …
            </div>
          ) : (pool.data?.items.length ?? 0) === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Noch keine Nummern im Pool.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nummer</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Land</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Zugewiesen an</TableHead>
                  <TableHead>Notiz</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(pool.data?.items ?? []).map((entry) => (
                  <PoolRow
                    key={entry.id}
                    entry={entry}
                    onAssign={() => setAssignTarget(entry)}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {buyDialogOpen && <BuyNumberDialog onClose={() => setBuyDialogOpen(false)} />}
      {assignTarget && (
        <AssignDialog
          entry={assignTarget}
          onClose={() => setAssignTarget(null)}
        />
      )}
    </div>
  );
}

function PoolStat({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number;
  tone?: 'default' | 'success' | 'primary' | 'muted';
}) {
  const toneClass =
    tone === 'success'
      ? 'text-emerald-400'
      : tone === 'primary'
        ? 'text-primary'
        : tone === 'muted'
          ? 'text-muted-foreground'
          : '';
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className={`mt-1 text-3xl font-semibold tabular-nums ${toneClass}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function PoolRow({
  entry,
  onAssign,
}: {
  entry: NumberPoolRow;
  onAssign: () => void;
}) {
  const release = useReleasePoolNumber();
  const unassign = useUnassignPoolNumber();
  const { toast } = useToast();

  return (
    <TableRow>
      <TableCell className="font-mono">{entry.e164}</TableCell>
      <TableCell>
        <Badge variant="outline">{entry.provider}</Badge>
      </TableCell>
      <TableCell>{entry.countryCode}</TableCell>
      <TableCell>
        <Badge
          variant={
            entry.status === 'available'
              ? 'success'
              : entry.status === 'assigned'
                ? 'default'
                : entry.status === 'released'
                  ? 'muted'
                  : 'warning'
          }
        >
          {entry.status}
        </Badge>
      </TableCell>
      <TableCell className="text-xs">
        {entry.assignedToUid ? (
          <Link
            to={`/admin/billing/${entry.assignedToUid}`}
            className="hover:underline"
          >
            {entry.assignedUserName || entry.assignedUserEmail || entry.assignedToUid}
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">{entry.notes}</TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          {entry.status === 'available' && (
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onAssign} title="Manuell zuweisen">
              <UserPlus className="h-3.5 w-3.5" />
            </Button>
          )}
          {entry.status === 'assigned' && (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={async () => {
                if (!confirm('Zuweisung wirklich aufheben?')) return;
                try {
                  await unassign.mutateAsync(entry.id);
                  toast({ title: 'Zuweisung aufgehoben' });
                } catch (err) {
                  toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
                }
              }}
              title="Zuweisung aufheben"
            >
              <Unlink className="h-3.5 w-3.5" />
            </Button>
          )}
          {entry.status !== 'released' && (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={async () => {
                if (!confirm(`Nummer ${entry.e164} wirklich releasen? Wird beim Carrier zurückgegeben.`)) return;
                try {
                  await release.mutateAsync(entry.id);
                  toast({ title: 'Nummer released' });
                } catch (err) {
                  toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
                }
              }}
              title="Nummer releasen"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Buy-number dialog — search + buy
// ─────────────────────────────────────────────────────────────────────

function BuyNumberDialog({ onClose }: { onClose: () => void }) {
  const search = useSearchPlatformNumbers();
  const buy = useBuyPoolNumber();
  const { toast } = useToast();

  const [provider, setProvider] = React.useState<'twilio' | 'telnyx'>('twilio');
  const [country, setCountry] = React.useState('AT');
  const [areaCode, setAreaCode] = React.useState('');
  const [contains, setContains] = React.useState('');
  const [results, setResults] = React.useState<AvailableNumberRow[]>([]);
  const [notes, setNotes] = React.useState('');
  const [busyPhone, setBusyPhone] = React.useState<string | null>(null);

  async function onSearch() {
    try {
      const r = await search.mutateAsync({
        provider,
        countryCode: country,
        areaCode: areaCode || undefined,
        contains: contains || undefined,
        limit: 20,
      });
      setResults(r.items);
      if (r.items.length === 0) {
        toast({ title: 'Keine Nummern gefunden', description: 'Anderen Filter probieren.' });
      }
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  async function onBuy(phoneNumber: string) {
    setBusyPhone(phoneNumber);
    try {
      await buy.mutateAsync({
        provider,
        phoneNumber,
        countryCode: country,
        notes,
      });
      toast({ title: 'Nummer gekauft', description: phoneNumber });
      setResults((prev) => prev.filter((r) => r.phoneNumber !== phoneNumber));
    } catch (err) {
      toast({ title: 'Kauf fehlgeschlagen', description: (err as Error).message, variant: 'destructive' });
    } finally {
      setBusyPhone(null);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Plattform-Nummer kaufen</DialogTitle>
          <DialogDescription>
            Sucht auf dem Plattform-Twilio bzw. -Telnyx-Account. Eingehende Anrufe werden
            automatisch auf die Voice-Agent-Webhooks gerouted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label>Provider</Label>
              <Select value={provider} onValueChange={(v) => setProvider(v as 'twilio' | 'telnyx')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="twilio">Twilio</SelectItem>
                  <SelectItem value="telnyx">Telnyx</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Land</Label>
              <Input value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} maxLength={2} />
            </div>
            <div className="space-y-1.5">
              <Label>Vorwahl</Label>
              <Input value={areaCode} onChange={(e) => setAreaCode(e.target.value)} placeholder="z.B. 1" />
            </div>
            <div className="space-y-1.5">
              <Label>Enthält</Label>
              <Input value={contains} onChange={(e) => setContains(e.target.value)} placeholder="z.B. 1234" />
            </div>
          </div>

          <Button onClick={() => void onSearch()} disabled={search.isPending}>
            {search.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
            Suchen
          </Button>

          {results.length > 0 && (
            <div className="space-y-2">
              <div className="space-y-1.5">
                <Label>Notiz für Pool-Eintrag</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="z.B. Wien-Local, AT-Mobile, …"
                />
              </div>
              <div className="rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nummer</TableHead>
                      <TableHead>Region</TableHead>
                      <TableHead>Land</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((r) => (
                      <TableRow key={r.phoneNumber}>
                        <TableCell className="font-mono">{r.phoneNumber}</TableCell>
                        <TableCell className="text-xs">
                          {r.locality || r.region || '—'}
                        </TableCell>
                        <TableCell className="text-xs">{r.isoCountry || country}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => void onBuy(r.phoneNumber)}
                            disabled={busyPhone === r.phoneNumber || buy.isPending}
                          >
                            {busyPhone === r.phoneNumber ? (
                              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Phone className="mr-1.5 h-3.5 w-3.5" />
                            )}
                            Kaufen
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Assign dialog
// ─────────────────────────────────────────────────────────────────────

function AssignDialog({
  entry,
  onClose,
}: {
  entry: NumberPoolRow;
  onClose: () => void;
}) {
  const users = useAdminUsers();
  const assign = useAssignPoolNumber();
  const { toast } = useToast();
  const [uid, setUid] = React.useState('');
  const [search, setSearch] = React.useState('');

  const filtered = (users.data ?? []).filter((u) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      u.email.toLowerCase().includes(q) ||
      (u.displayName ?? '').toLowerCase().includes(q) ||
      (u.firstName ?? '').toLowerCase().includes(q) ||
      (u.lastName ?? '').toLowerCase().includes(q)
    );
  });

  async function onAssign() {
    if (!uid) return;
    try {
      await assign.mutateAsync({ id: entry.id, uid });
      toast({ title: 'Zugewiesen' });
      onClose();
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nummer manuell zuweisen</DialogTitle>
          <DialogDescription>
            <span className="font-mono">{entry.e164}</span> einem Benutzer zuordnen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            placeholder="Suchen …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="max-h-72 overflow-auto rounded-md border border-border">
            {filtered.map((u) => (
              <button
                key={u.uid}
                type="button"
                onClick={() => setUid(u.uid)}
                className={`flex w-full items-center justify-between gap-2 border-b border-border px-3 py-2 text-left text-sm last:border-0 hover:bg-muted/40 ${
                  u.uid === uid ? 'bg-muted/60' : ''
                }`}
              >
                <span>
                  <span className="block font-medium">
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.displayName || '—'}
                  </span>
                  <span className="block text-xs text-muted-foreground">{u.email}</span>
                </span>
                {u.uid === uid && <CheckCircle2 className="h-4 w-4 text-primary" />}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="p-3 text-center text-xs text-muted-foreground">
                Keine Treffer.
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button disabled={!uid || assign.isPending} onClick={() => void onAssign()}>
            {assign.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
            Zuweisen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Suppress unused
void Copy;
void ExternalLink;
void PhoneCall;
